import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import TopBar from "./components/TopBar";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import CustomCursor from "./components/ui/CustomCursor";
import ScrollToTopButton from "./components/ScrollToTopButton";
import { fetchBannerImages } from "./api/api";
import { socket } from "./utils/socket";
import { getAssetUrl } from "./utils/assets";
import { preloadImage } from "./utils/preloadImage";

const emptyBannerSlots = [
  { id: 0, image: null },
  { id: 1, image: null },
];

const Trades = lazy(() => import("./pages/Trades"));
const Faculties = lazy(() => import("./pages/Faculties"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contact = lazy(() => import("./pages/Contact"));

function AppShell() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const [bannerImages, setBannerImages] = useState([]);
  const banner2Image = getAssetUrl(bannerImages?.[1]?.image);

  const fetchBanners = useCallback(async () => {
    try {
      const nextBannerImages = await fetchBannerImages();
      if (Array.isArray(nextBannerImages)) {
        setBannerImages(nextBannerImages);
        preloadImage(getAssetUrl(nextBannerImages?.[0]?.image));
        return;
      }
    } catch {
      /* keep last successful banner state */
    }

    setBannerImages((current) => (Array.isArray(current) && current.length ? current : emptyBannerSlots));
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("reveal-ready");
    const nodes = document.querySelectorAll(".reveal-on-scroll");
    if (!nodes.length) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-revealed");
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -12% 0px" }
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [location.pathname, banner2Image]);

  useEffect(() => {
    fetchBanners();
  }, [fetchBanners]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    if (!banner2Image) return undefined;

    const prefetchBanner2 = () => {
      preloadImage(banner2Image);
      const img = new Image();
      img.decoding = "async";
      img.src = banner2Image;
    };

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(prefetchBanner2, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(idleId);
    }

    const timeoutId = window.setTimeout(prefetchBanner2, 0);
    return () => window.clearTimeout(timeoutId);
  }, [banner2Image]);

  useEffect(() => {
    const handleBannerUpdated = () => {
      fetchBanners();
    };

    socket.on("banner-updated", handleBannerUpdated);

    return () => {
      socket.off("banner-updated", handleBannerUpdated);
    };
  }, [fetchBanners]);

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      {!isAdminRoute ? <CustomCursor enabled /> : null}
      {!isAdminRoute ? <TopBar /> : null}
      {!isAdminRoute ? <Navbar /> : null}
      <main key={location.pathname} className={`${isAdminRoute ? "main-admin" : ""} page-transition-enter`}>
        <Suspense fallback={<div className="container py-10 text-center text-slate-500">Loading…</div>}>
        <Routes>
          <Route path="/" element={<Home bannerImages={bannerImages} />} />
          <Route path="/trades" element={<Trades />} />
          <Route path="/faculties" element={<Faculties />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
      </main>
      {!isAdminRoute ? <ScrollToTopButton /> : null}
      {!isAdminRoute ? <Footer /> : null}
    </GoogleOAuthProvider>
  );
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
